/**
 * AdminLiveSmoke — /admin/live-smoke
 *
 * Tooling page for the founder admin to safely run a live (production)
 * smoke test using the existing live-smoke guardrail endpoints:
 *
 *   • POST   /api/beta/admin/invites          (mint codes — live_smoke_* waves auto-expire in 24h)
 *   • GET    /api/beta/admin/invites          (list — filter client-side to live_smoke_*)
 *   • GET    /api/beta/admin/test-accounts    (read-only preview of test_account: true users)
 *   • DELETE /api/beta/admin/test-accounts    (one-click cleanup)
 *
 * Admin-only — wrapped in <RoleRoute allowedRoles={["admin"]}> in App.js.
 *
 * Hard warning banner is permanent on the page so this is never confused
 * with the real cohort invite flow at /admin/beta.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, Copy, Check, KeyRound, Trash2,
  RefreshCw, Users, Clock,
} from "lucide-react";
import { MobileDialog } from "../components/mobile";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function expiryBadge(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return { label: "EXPIRED", tone: "rose" };
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return { label: `${h}h ${m}m left`, tone: h < 2 ? "amber" : "emerald" };
}

function todayWave() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `live_smoke_${y}_${m}_${day}`;
}

function CopyButton({ value, label = "Copy", icon = "copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={`live-smoke-copy-${icon}-${value}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch { /* ignore — clipboard not available */ }
      }}
      className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-300 hover:text-white px-2 py-1 rounded border border-white/10 hover:border-white/25"
    >
      {done ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {done ? "Copied" : label}
    </button>
  );
}

export default function AdminLiveSmoke() {
  const [wave, setWave] = useState(todayWave());
  const [busyRole, setBusyRole] = useState(null);
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [inv, ta] = await Promise.all([
        axios.get(`${API}/api/beta/admin/invites`, { withCredentials: true }),
        axios.get(`${API}/api/beta/admin/test-accounts`, { withCredentials: true }),
      ]);
      setInvites(inv.data?.items || []);
      setUsers(ta.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load — are you signed in as admin?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const liveSmokeInvites = useMemo(
    () => invites.filter((i) => (i.wave || "").toLowerCase().startsWith("live_smoke")),
    [invites],
  );

  const mint = async (role) => {
    const w = (wave || "").trim();
    if (!w.toLowerCase().startsWith("live_smoke")) {
      toast.error("Wave name must start with `live_smoke` — change the field above first.");
      return;
    }
    setBusyRole(role);
    try {
      const { data } = await axios.post(
        `${API}/api/beta/admin/invites`,
        { role, wave: w, note: "live smoke (admin UI)" },
        { withCredentials: true },
      );
      toast.success(`${role.toUpperCase()} code minted: ${data.code}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to mint ${role} code`);
    } finally {
      setBusyRole(null);
    }
  };

  const cleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await axios.delete(
        `${API}/api/beta/admin/test-accounts`,
        { withCredentials: true },
      );
      setCleanupReport(data);
      toast.success(`Removed ${data.deleted_users} test account(s).`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div data-testid="admin-live-smoke-page" className="min-h-dvh no-overflow-x bg-[#050505] text-white">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        {/* Hard warning banner */}
        <div
          data-testid="live-smoke-warning"
          className="rounded-xl border border-rose-500/40 bg-rose-500/[0.06] p-4 md:p-5 mb-8 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-rose-300 mt-0.5 flex-shrink-0" />
          <div className="text-[13px] leading-relaxed text-rose-100">
            <div className="font-bold text-rose-50 mb-1">For live smoke / testing only.</div>
            Codes minted here auto-expire in <strong>24 hours</strong> and any accounts
            registered through them are flagged <code className="font-mono text-rose-200">test_account: true</code>{" "}
            so they can be wiped with one click below. <strong>Never use this page</strong> to
            invite real cohort instructors — use <a href="/admin/beta" className="underline underline-offset-2">/admin/beta</a> for that.
          </div>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Live Smoke Toolkit</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Mint isolated test accounts on production, smoke-test the role gates, then nuke them.
            </p>
          </div>
          <button
            data-testid="live-smoke-refresh"
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/25 text-[12px] font-bold disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Step 1 — Mint codes */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center text-[11px] font-bold text-[#F3E5AB]">1</span>
            <h2 className="text-lg font-bold">Mint live-smoke codes</h2>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">Wave name (must start with <code className="font-mono">live_smoke</code>)</label>
            <input
              data-testid="live-smoke-wave-input"
              value={wave}
              onChange={(e) => setWave(e.target.value)}
              className="w-full md:max-w-md h-10 rounded-md bg-[#0a0a0a] border border-white/10 focus:border-[#D4AF37]/50 px-3 text-sm font-mono outline-none"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              {["adi", "pdi", "learner"].map((role) => (
                <button
                  key={role}
                  data-testid={`live-smoke-mint-${role}`}
                  onClick={() => mint(role)}
                  disabled={busyRole === role}
                  className="h-11 rounded-lg border border-[#D4AF37]/35 bg-[#D4AF37]/[0.06] hover:bg-[#D4AF37]/[0.12] text-[#F3E5AB] text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  {busyRole === role ? "Minting…" : `Mint ${role.toUpperCase()} code`}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Step 2 — Active live-smoke codes */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center text-[11px] font-bold text-[#F3E5AB]">2</span>
            <h2 className="text-lg font-bold">Active live-smoke codes</h2>
            <span data-testid="live-smoke-codes-count" className="text-[11px] text-zinc-500">({liveSmokeInvites.length})</span>
          </div>
          {liveSmokeInvites.length === 0 ? (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-sm text-zinc-500">
              No live-smoke codes yet. Mint one above.
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-white/[0.03] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Code</th>
                    <th className="text-left px-4 py-3 font-bold">Role</th>
                    <th className="text-left px-4 py-3 font-bold">Wave</th>
                    <th className="text-left px-4 py-3 font-bold">Expires</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="text-right px-4 py-3 font-bold"></th>
                  </tr>
                </thead>
                <tbody>
                  {liveSmokeInvites.map((inv) => {
                    const badge = expiryBadge(inv.expires_at);
                    const redeemed = !!inv.redeemed_at;
                    return (
                      <tr key={inv.code} data-testid={`live-smoke-code-row-${inv.code}`} className="border-t border-white/[0.05]">
                        <td className="px-4 py-3 font-mono text-[#F3E5AB]">{inv.code}</td>
                        <td className="px-4 py-3 uppercase text-[11px] font-bold text-zinc-300">{inv.role}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-zinc-400 truncate max-w-[160px]">{inv.wave}</td>
                        <td className="px-4 py-3 text-zinc-400">
                          {badge ? (
                            <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                              badge.tone === "emerald" ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
                              : badge.tone === "amber" ? "border-amber-500/35 bg-amber-500/10 text-amber-200"
                              : "border-rose-500/35 bg-rose-500/10 text-rose-200"
                            }`}>
                              <Clock className="w-2.5 h-2.5" /> {badge.label}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {redeemed ? (
                            <span className="text-[11px] text-emerald-300 font-bold uppercase">Redeemed</span>
                          ) : (
                            <span className="text-[11px] text-zinc-400">Unused</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!redeemed && (
                            <div className="inline-flex items-center gap-1.5 justify-end">
                              <CopyButton value={inv.code} label="Code" icon="code" />
                              <CopyButton
                                value={`${typeof window !== "undefined" ? window.location.origin : ""}/register?beta_code=${inv.code}`}
                                label="Reg URL"
                                icon="url"
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-zinc-500 mt-2">
            Register at <code className="font-mono text-zinc-400">/register?beta_code=BETA-XXXXXXXX</code> with the matching role.
          </p>
        </section>

        {/* Step 3 — Test accounts */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center text-[11px] font-bold text-[#F3E5AB]">3</span>
            <h2 className="text-lg font-bold">Test accounts on this environment</h2>
            <span data-testid="live-smoke-users-count" className="text-[11px] text-zinc-500">({users.length})</span>
          </div>
          {users.length === 0 ? (
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-sm text-zinc-500">
              No <code className="font-mono text-zinc-400">test_account: true</code> users.
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-white/[0.03] text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">Email</th>
                    <th className="text-left px-4 py-3 font-bold">Role</th>
                    <th className="text-left px-4 py-3 font-bold">Wave</th>
                    <th className="text-left px-4 py-3 font-bold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.email} data-testid={`live-smoke-user-row-${u.email}`} className="border-t border-white/[0.05]">
                      <td className="px-4 py-3 font-mono">{u.email}</td>
                      <td className="px-4 py-3 uppercase text-[11px] font-bold text-zinc-300">{u.role}</td>
                      <td className="px-4 py-3 font-mono text-[11px] text-zinc-400 truncate max-w-[180px]">{u.test_account_wave || u.beta_wave || "—"}</td>
                      <td className="px-4 py-3 text-zinc-400 text-[11.5px]">{fmtWhen(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Step 4 — Cleanup */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center text-[11px] font-bold text-rose-200">4</span>
            <h2 className="text-lg font-bold">Cleanup</h2>
          </div>
          <div className="rounded-xl border border-rose-500/25 bg-rose-500/[0.04] p-5 flex items-start gap-4">
            <Trash2 className="w-5 h-5 text-rose-300 mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-zinc-200 leading-relaxed">
                Removes <strong>{users.length}</strong> test account(s) and cascades through related
                operational rows (clip baskets, lesson reviews, notifications, work students, bookings,
                auth events, …). Founder admin is excluded by hard filter — it is never deletable here.
              </p>
              <button
                data-testid="live-smoke-cleanup-btn"
                onClick={() => setConfirmOpen(true)}
                disabled={users.length === 0}
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-500/15 border border-rose-500/40 hover:bg-rose-500/25 text-rose-100 text-[13px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
                {users.length === 0 ? "Nothing to clean" : `Wipe ${users.length} test account${users.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>

          {cleanupReport && (
            <div data-testid="live-smoke-cleanup-report" className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12.5px] text-zinc-300">
              <div className="font-bold text-zinc-100 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-300" />
                Cleanup receipt — {cleanupReport.deleted_users} user(s) removed
              </div>
              {Object.keys(cleanupReport.cascade || {}).length > 0 && (
                <ul className="ml-6 list-disc text-zinc-400 space-y-0.5">
                  {Object.entries(cleanupReport.cascade).map(([col, n]) => (
                    <li key={col}><code className="font-mono">{col}</code>: {n}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Confirm modal */}
      {confirmOpen && (
        <MobileDialog
          bareContent
          open={confirmOpen}
          onClose={() => !cleaning && setConfirmOpen(false)}
          dismissible={!cleaning}
          testid="live-smoke-confirm-modal-wrap"
          presentation="center"
        >
          <div
            data-testid="live-smoke-confirm-modal"
            className="w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/10 p-6 mx-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-rose-300" />
              <h3 className="text-base font-bold">Confirm cleanup</h3>
            </div>
            <p className="text-[13px] text-zinc-300 leading-relaxed">
              This will permanently delete <strong>{users.length} test account{users.length === 1 ? "" : "s"}</strong> and
              cascade through all related operational rows. This action cannot be undone.
            </p>
            <div className="flex gap-2 mt-5">
              <button
                data-testid="live-smoke-confirm-cancel"
                onClick={() => setConfirmOpen(false)}
                disabled={cleaning}
                className="flex-1 h-10 rounded-lg border border-white/10 hover:border-white/25 text-[13px] font-bold"
              >
                Cancel
              </button>
              <button
                data-testid="live-smoke-confirm-delete"
                onClick={cleanup}
                disabled={cleaning}
                className="flex-1 h-10 rounded-lg bg-rose-500 hover:bg-rose-400 text-white text-[13px] font-bold disabled:opacity-60"
              >
                {cleaning ? "Wiping…" : "Yes, wipe them"}
              </button>
            </div>
          </div>
        </MobileDialog>
      )}
    </div>
  );
}
